import { ParsedUrlQuery } from 'querystring'
import { Box, Stack } from '@mui/material'
import { FeatureFlagIdShowProfileToEndUsers, Features } from 'c1gen/features'
import { C1Chip } from 'c1ui/components/C1Chip'
import { C1CircularProgress } from 'c1ui/components/C1CircularProgress/C1CircularProgress'
import { APIError } from 'components/core/errors/api'
import { SmartTabs, SmartTabsWithRouting, TabLabel, TabWithFeature } from 'components/core/tabs'
import { TaskAuditV2 } from 'components/pages/task/audit/TaskAuditV2'
import { TaskAdditionalInfo } from 'components/pages/task/details/content/TaskAdditionalInfo'
import { TaskComments } from 'components/pages/task/details/content/TaskComments'
import { TaskCurrentStep } from 'components/pages/task/details/content/TaskCurrentStep'
import { TaskExecutionPlan } from 'components/pages/task/details/content/TaskExecutionPlan'
import { TaskInsightsTab } from 'components/pages/task/details/content/TaskInsightsTab'
import { TaskTitle } from 'components/pages/task/details/content/TaskTitle'
import { TaskWaitingOn } from 'components/pages/task/details/content/TaskWaitingOn'
import { TaskDrawerHeader } from 'components/pages/task/details/drawer/TaskDrawer'
import { RelatedTaskListV2 } from 'components/pages/task/details/task-content-v2/related-tasks/RelatedTaskListV2'
import { TaskValue } from 'lib/actions/task'
import { DisplayModeContext } from 'lib/context/app'
import { TaskActionsServiceApproveWithStepUpRequest } from 'pbts/c1/api/task/v1/actions'
import { CountRelatedTasksRequest } from 'pbts/c1/api/task/v1/binding'
import { InsightType } from 'pbts/c1/api/task/v1/insights'
import { SearchWaitingOnRequest } from 'pbts/c1/api/task/v1/search'
import { FC, useContext, useEffect, useMemo, useState } from 'react'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'
import { approveWithStepUpTask, countRelatedTasksApi, tasksSearchWaitingOnApi } from 'redux_toolkit/tasks'
import { TaskRequestedData } from './TaskRequestedData'
import { TaskUserInfo } from './TaskUserInfo'

type TaskContentProps = {
    task: TaskValue
    stepUpTxn?: string
    stepUpCompleted?: () => void
    hasPendingActions?: boolean
}

const getTabs = (
    task: TaskValue,
    intl: IntlShape,
    relatedTaskCount: number,
): TabWithFeature[] => {
    const insightsCount = task.insights?.length ?? 0
    const hasAlertInsights = task.insights?.some((i) => i.type === InsightType.ALERT) ?? false
    const hasNegativeInsights = task.insights?.some((i) => i.type === InsightType.NEGATIVE) ?? false

    return [
        {
            key: 'details',
            title: intl.formatMessage({ id: 'components_task_details_content_tab_details', defaultMessage: 'Details' }),
            tab: <Stack pt={2} gap={4}>
                <TaskAdditionalInfo task={task} />
                <TaskRequestedData task={task} />
                <TaskComments task={task} />
            </Stack>,
        },
        {
            key: 'insights',
            title: <Stack direction="row" spacing={1} alignItems="center">
                <Box><FormattedMessage id="components_task_details_content_tab_insights" defaultMessage="Insights" /></Box>
                {insightsCount > 0 && (
                    <C1Chip
                        label={insightsCount}
                        size="extraSmall"
                        color={hasAlertInsights ? 'error' : hasNegativeInsights ? 'warning' : 'info'}
                    />
                )}
            </Stack>,
            tab: <TaskInsightsTab task={task} />,
            disabled: insightsCount === 0,
        },
        ...(task.user ? [{
            key: 'user-info',
            title: intl.formatMessage({ id: 'components_task_details_content_tab_user_info', defaultMessage: 'User info' }),
            tab: <TaskUserInfo task={task} />,
            feature: FeatureFlagIdShowProfileToEndUsers as Features,
        }] : []),
        {
            key: 'execution-plan',
            title: intl.formatMessage({ id: 'components_task_details_content_tab_execution_plan', defaultMessage: 'Execution plan' }),
            tab: <TaskExecutionPlan task={task} />,
        },
        {
            key: 'related-tasks',
            title: <TabLabel
                label={intl.formatMessage({ id: 'components_task_details_content_tab_related_tasks', defaultMessage: 'Related tasks' })}
                count={relatedTaskCount}
            />,
            tab: <RelatedTaskListV2 taskId={task.id} />,
            disabled: relatedTaskCount === 0,
        },
        {
            key: 'audit-log',
            title: intl.formatMessage({ id: 'components_task_details_content_tab_audit_log', defaultMessage: 'Audit log' }),
            tab: <TaskAuditV2 taskId={task.id} task={task} />,
        },
    ]
}

export const TaskContent: FC<TaskContentProps> = ({ task, stepUpTxn, stepUpCompleted, hasPendingActions = false }) => {
    const intl = useIntl()
    const { trigger: stepUpTrigger, data: stepUpData, error: stepUpError } = approveWithStepUpTask.useLazyApproveWithStepUp()
    const displayContext = useContext(DisplayModeContext)
    const isInModal = displayContext === 'dialog' || displayContext === 'drawer'

    // Add state for loading during step-up authentication
    const [isStepUpLoading, setIsStepUpLoading] = useState<boolean>(false)

    // Effect to handle step-up transaction if provided
    useEffect(() => {
        if (!stepUpTxn || !task.policy?.current?.id || !task.id) {
            return
        }

        setIsStepUpLoading(true)
        stepUpTrigger(
            TaskActionsServiceApproveWithStepUpRequest.create({
                taskId: task.id,
                stepUpTransactionId: stepUpTxn,
                policyStepId: task.policy?.current?.id,
            }),
        )
    }, [stepUpTxn, task.id, task.policy, stepUpTrigger])

    useEffect(() => {
        if (stepUpData?.stepUpRedirectUrl) {
            window.location.href = stepUpData.stepUpRedirectUrl
        }
        stepUpCompleted?.()
        setIsStepUpLoading(false)
    }, [stepUpData, stepUpCompleted])

    const tabChange = (newValue: string): string => `/task/${task.id}/${newValue}`

    const tabFromRouterQuery = (query: ParsedUrlQuery): string | string[] | undefined => {
        const { task_tab } = query
        return task_tab
    }

    const { data: relatedTaskCount } = countRelatedTasksApi.useCountRelatedTasks(
        CountRelatedTasksRequest.create({ referenceTaskId: task.id }),
    )

    const {
        data: waitingOnTasks,
        isLoading: waitingOnTasksIsLoading,
        error: waitingOnTasksError,
    } = tasksSearchWaitingOnApi.useSearchWaitingOn(SearchWaitingOnRequest.create({ taskId: task.id }))

    const tabs = useMemo(() => getTabs(task, intl, relatedTaskCount?.count ?? 0), [task, intl, relatedTaskCount])

    // If we're in step-up loading state, show skeleton loading for the entire component
    if (isStepUpLoading) {
        return (
            <Stack spacing={3} alignItems="center" justifyContent="center" sx={{ py: 4 }}>
                {stepUpError && <APIError err={stepUpError} />}
                {!stepUpError && <C1CircularProgress />}
            </Stack>
        )
    }

    return <>
        <TaskDrawerHeader task={task} hasPendingActions={hasPendingActions} />
        <Stack spacing={5}>

            <TaskTitle task={task} hasPendingActions={hasPendingActions} />

            <APIError err={waitingOnTasksError} />

            {waitingOnTasksIsLoading ? (
                <C1CircularProgress />
            ) : (
                waitingOnTasks && waitingOnTasks.length > 0 ? (
                    <TaskWaitingOn waitingOnTasks={waitingOnTasks} />
                ) : (
                    <TaskCurrentStep task={task} hasPendingActions={hasPendingActions} />
                )
            )}

            {!isInModal && <SmartTabsWithRouting
                menuKey="task-details-tabs"
                tabs={tabs}
                onTabChange={tabChange}
                defaultTabKey="details"
                tabFromRouterQuery={tabFromRouterQuery}
            />}

            {isInModal && <SmartTabs
                menuKey="task-details-tabs"
                defaultTab="details"
                tabs={tabs}
                disableScrollIntoViewBehavior
            />}
        </Stack>
    </>
}
